import {
    type AccountSummary,
    type Cents,
    DEFAULT_VALUE_PLACEHOLDER,
    type DepositAccount,
    type FormattedDollars,
    HolisticCategory,
    MoneyMapRuleType,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
    Provider,
    RecurringTransferEndType,
} from '@fruitful/types';
import {
    CalendarClockIcon,
    CircleDollarSignIcon,
    CreditCardIcon,
    DownloadIcon,
    type IconType,
    UploadIcon,
    WalletIcon,
} from 'smoothie';

import {
    type AccountDetail,
    AccountType,
    type BankAccountDisplayDetail,
    DEFAULT_INDIVIDUAL_CHECKING_ACCOUNT_NICKNAME,
    DEFAULT_JOINT_CHECKING_ACCOUNT_NICKNAME,
    DEFAULT_SAVINGS_ACCOUNT_NICKNAME,
    KYCVerificationStatus,
    type UserInfoSummary,
} from '../../banking/types/BankAccount';
import {
    CardColor,
    CardForm,
    CardStatus,
    UserCardStatusReason,
} from '../../banking/types/Card';
import {
    type LegacyTransaction,
    type Transaction,
    TransactionDcSign,
} from '../../banking/types/Transactions';
import type { User } from '../../user/types/User';
import { assertNotNullish, convertToNegative, isEmpty } from '../misc';
import { formatFullName, isActiveMember } from './userUtils';

export const MINIMUM_TRANSFER_AMOUNT_CENTS = 1;

export const NOT_CURRENCY_CHARACTERS_REGEX = /[^-?\d+(\\.\d{2})?]/g;
export const THOUSANDS_PLACE_COMMAS_REGEX = /\B(?=(\d{3})+(?!\d))/g;
export const FORMATTED_CURRENCY_REGEX =
    /^(\$?[0-9])(\d{0,2}(?:,\d{3})*(?:\.\d{0,2})?)$/;

export enum BankAccountCreationDisabledReason {
    MAX_LIMIT = 'MAX_LIMIT',
    INACTIVE_MEMBERSHIP = 'INACTIVE_MEMBERSHIP',
    KYC_STATUS = 'KYC_STATUS',
}

export const canUserCreateBankAccount = (
    user: User,
): { allowed: boolean; reason?: BankAccountCreationDisabledReason } => {
    const { syncteraKycStatus: kycStatus, hasMaxBankAccounts } = user;

    if (!isActiveMember(user)) {
        return {
            allowed: false,
            reason: BankAccountCreationDisabledReason.INACTIVE_MEMBERSHIP,
        };
    }

    if (hasMaxBankAccounts) {
        return {
            allowed: false,
            reason: BankAccountCreationDisabledReason.MAX_LIMIT,
        };
    }

    const allowedStatuses = [
        KYCVerificationStatus.ACCEPTED,
        KYCVerificationStatus.PROVISIONAL,
    ];

    if (kycStatus == null || !allowedStatuses.includes(kycStatus)) {
        return {
            allowed: false,
            reason: BankAccountCreationDisabledReason.KYC_STATUS,
        };
    }

    return { allowed: true };
};

export const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
};

export const centsToDollars = (cents: number): number => {
    return roundToTwoDecimals(cents / 100);
};

export const formatDollars = (
    dollarValue: number = 0,
    showDecimals = true,
): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...(showDecimals
            ? {}
            : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    });
    return formatter.format(dollarValue);
};

export const centsToFormattedDollars = (value: Cents): FormattedDollars => {
    return formatDollars(centsToDollars(value));
};

export const formattedDollarsToCents = (formattedDollars: string): Cents => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    const unformattedDollars = formattedDollars.replace(
        NOT_CURRENCY_CHARACTERS_REGEX,
        '',
    );

    // Round to handle any floating point precision issues
    return Math.round(parseFloat(unformattedDollars) * 100);
};

export const formattedDollarsToFloat = (formattedDollars: string): Cents => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    const unformattedDollars = formattedDollars.replace(
        NOT_CURRENCY_CHARACTERS_REGEX,
        '',
    );

    return parseFloat(unformattedDollars);
};

export const formatCurrencyInputOnChange = (
    previousValue: string,
    nextValue: string,
) => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    nextValue = nextValue.replace(NOT_CURRENCY_CHARACTERS_REGEX, '');

    if (isEmpty(nextValue)) return nextValue;

    // Add in commas every thousand's place
    const formattedValue = nextValue.replace(THOUSANDS_PLACE_COMMAS_REGEX, ',');

    // If we don't have a valid formatted number, return the previous value
    if (!FORMATTED_CURRENCY_REGEX.exec(formattedValue)) {
        return previousValue;
    } else {
        return `$${formattedValue}`;
    }
};

export const getPrettyAccountType = (account: AccountSummary): string => {
    const { type, provider } = account;

    if (type == null) return 'Unknown';

    if (provider === Provider.ORION || provider === Provider.SYNCTERA) {
        // Orion and synctera account types are human-readable already
        return type;
    }

    return 'Unknown';
};

export const formatInterestRateOrYieldPercent = (value: number): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return formatter.format(value);
};

export const maskValue = ({
    value,
    maskedLength,
    maskedCharacter = '*',
}: {
    value: string | number;
    maskedLength: number;
    maskedCharacter?: string;
}) => {
    const valueString = value.toString();
    const remainingValue = valueString.slice(maskedLength);
    const maskedValue = Array.from({ length: maskedLength })
        .map(() => maskedCharacter)
        .join('');

    return `${maskedValue}${remainingValue}`;
};

export const formatMaskedValue = (
    maskedValue?: string,
    maskedCharacter = '*',
) => {
    if (!isEmpty(maskedValue)) {
        return `${maskedCharacter}${maskedValue}`;
    }
    return '';
};

export const getHolisticCategoryForBankAccount = (
    account: AccountDetail,
): HolisticCategory => {
    const { holisticCategory, accountType } = account;
    return (
        holisticCategory ??
        (accountType === AccountType.SAVING
            ? HolisticCategory.SAVE
            : HolisticCategory.SPEND)
    );
};

export const transformBankAccountDetails = (
    accountDetails: AccountDetail,
): BankAccountDisplayDetail => {
    const {
        bankAccountId,
        accountType,
        syncteraAccountId,
        chargeSecuredSyncteraAccountId,
        primaryUser,
        jointUser,
        jointUserRelationshipStatus,
        currentBalanceCents,
        availableToWithdrawCents,
        cardBalanceCents,
        interestEarnedCents,
        cashBackEarnedCents,
        pendingTransfersCents,
        pendingCardTransactionsCents,
        nickname,
        icon,
        color,
        autoPayEnabled,
        autoPaymentDate,
        paymentDueDate,
        balanceDueCents,
        daysPastDue,
        cards,
        accountStatus,
        creationTime,
        interestRate,
        apy,
    } = accountDetails ?? {};

    const availableToWithdraw =
        availableToWithdrawCents != null
            ? centsToFormattedDollars(availableToWithdrawCents)
            : `$${DEFAULT_VALUE_PLACEHOLDER}`;

    const currentBalance = centsToFormattedDollars(currentBalanceCents ?? 0);
    const interestEarned = centsToFormattedDollars(interestEarnedCents ?? 0);
    const pendingTransfers = centsToFormattedDollars(
        pendingTransfersCents ?? 0,
    );

    // Add card related balances optionally on account display object (not every account has a card)
    const cardBalance =
        cardBalanceCents != null
            ? // Display card balance as a negative value
              centsToFormattedDollars(convertToNegative(cardBalanceCents))
            : null;
    const cashBackEarned =
        cashBackEarnedCents != null
            ? centsToFormattedDollars(cashBackEarnedCents ?? 0)
            : null;
    const pendingCardTransactions =
        pendingCardTransactionsCents != null
            ? // Display as a negative value
              centsToFormattedDollars(
                  convertToNegative(pendingCardTransactionsCents),
              )
            : null;
    const balanceDue =
        balanceDueCents != null
            ? centsToFormattedDollars(balanceDueCents ?? 0)
            : null;

    const accountNumber =
        accountDetails?.accountNumber ?? DEFAULT_VALUE_PLACEHOLDER;

    const accountNumberMasked =
        accountDetails?.accountNumberMasked ?? DEFAULT_VALUE_PLACEHOLDER;

    const routingNumber =
        accountDetails?.routingNumber ?? DEFAULT_VALUE_PLACEHOLDER;

    const hasPendingTransactions =
        currentBalanceCents > availableToWithdrawCents;

    const routingNumberMasked = maskValue({
        value: routingNumber,
        maskedLength: 4,
    });

    const hasCard =
        (cards ?? []).filter(
            (card) => card.cardStatus !== CardStatus.TERMINATED,
        ).length > 0;

    return {
        bankAccountId,
        accountType,
        syncteraAccountId,
        chargeSecuredSyncteraAccountId,
        primaryUser,
        jointUser,
        jointUserRelationshipStatus,
        accountNumber,
        accountNumberMasked,
        availableToWithdraw,
        currentBalance,
        interestEarned,
        pendingTransfers,
        ...(cardBalance != null ? { cardBalance } : {}),
        ...(balanceDue != null ? { balanceDue } : {}),
        ...(cashBackEarned != null ? { cashBackEarned } : {}),
        ...(pendingCardTransactions != null ? { pendingCardTransactions } : {}),
        hasPendingTransactions,
        routingNumber,
        routingNumberMasked,
        nickname,
        icon,
        color,
        holisticCategory: getHolisticCategoryForBankAccount(accountDetails),
        autoPayEnabled,
        autoPaymentDate,
        paymentDueDate,
        daysPastDue,
        hasCard,
        cards,
        accountStatus,
        creationTime,
        interestRate,
        apy,
    };
};

/**
 * Retrieves the preferred name for a user, prioritizing legal names over regular names.
 *
 * @param user - An object containing user information, including first name, last name, and their legal equivalents.
 * @returns An array containing two strings: the preferred first name and the preferred last name.
 *
 * This function prioritizes legal names to ensure compliance with financial regulations when displaying user names.
 * It uses the legal names from Synctera if they exist and are not empty, otherwise it uses the member's regular names.
 * Both names are passed through the assertNotNullish function to ensure they are not null.
 */
export const preferLegalName = (user: UserInfoSummary): [string, string] => {
    const firstName = !isEmpty(user.legalFirstName)
        ? user.legalFirstName
        : user.firstName;
    const lastName = !isEmpty(user.legalLastName)
        ? user.legalLastName
        : user.lastName;
    return [assertNotNullish(firstName), assertNotNullish(lastName)];
};

export const getPreferredLegalNameFormatted = (
    user: UserInfoSummary,
): string => {
    return formatFullName(...preferLegalName(user));
};

export const getAccountHolders = (
    primaryUser: UserInfoSummary,
    jointUser?: UserInfoSummary | null,
) => {
    const primaryName = getPreferredLegalNameFormatted(primaryUser);
    const jointName =
        jointUser != null ? getPreferredLegalNameFormatted(jointUser) : null;
    return `${primaryName}${jointName != null ? `, ${jointName}` : ''}`;
};

export const isInterestPayoutTransaction = (
    transaction: LegacyTransaction,
): boolean => {
    return /interest\spayout/gi.test(transaction.description);
};

export const getRecurringTransferEndText = (
    endType?: RecurringTransferEndType,
    formattedEndDate?: string | null,
    endCount?: number | null,
): string => {
    if (endType === RecurringTransferEndType.NEVER) {
        return 'Never';
    }

    if (formattedEndDate != null) {
        return formattedEndDate;
    }

    if (endCount != null) {
        return `After ${endCount} transfers `;
    }

    return '';
};

export const getNormalizedDepositAccountNickname = (
    depositAccount: DepositAccount,
): string => {
    const { accountType, nickname } = depositAccount;
    return !isEmpty(nickname) && !nickname.includes('Plaid')
        ? nickname
        : accountType;
};

export const getDefaultedAccountNickname = <
    T extends Partial<BankAccountDisplayDetail>,
>(
    account: T,
): string => {
    const { accountType, nickname, jointUser } = account;

    if (!isEmpty(nickname)) return nickname as string;

    if (accountType === AccountType.SAVING)
        return DEFAULT_SAVINGS_ACCOUNT_NICKNAME;

    return jointUser == null
        ? DEFAULT_INDIVIDUAL_CHECKING_ACCOUNT_NICKNAME
        : DEFAULT_JOINT_CHECKING_ACCOUNT_NICKNAME;
};

export const canRequestCard = (
    syncteraPersonId: string,
    { cards, accountType }: BankAccountDisplayDetail,
): boolean => {
    // Don't use the util from cardUtils to avoid circular dependency
    const nonTerminatedCardsForUser = (cards ?? []).filter(
        ({ cardStatus, syncteraPersonId: cardSyncteraPersonId }) =>
            cardStatus !== CardStatus.TERMINATED &&
            cardSyncteraPersonId === syncteraPersonId,
    );

    return (
        nonTerminatedCardsForUser.length === 0 &&
        accountType === AccountType.CHECKING
    );
};

export const getTransactionIcon = (transaction: Transaction): IconType => {
    const { type, subtype, dcSign, isPayment } = transaction;

    if (subtype === 'atm_withdrawal' || subtype === 'jit_fund') {
        return WalletIcon;
    } else if (isPayment) {
        return CalendarClockIcon;
    } else if (type === 'card' || type === 'dispute') {
        return CreditCardIcon;
    } else if (subtype === 'interest_payout' || subtype === 'cashback') {
        return CircleDollarSignIcon;
    } else if (dcSign === TransactionDcSign.CREDIT.toLowerCase()) {
        return DownloadIcon;
    } else {
        return UploadIcon;
    }
};

export const emphasizeCardStatus = (cardStatus?: CardStatus) => {
    return (
        cardStatus != null &&
        [
            CardStatus.UNACTIVATED,
            CardStatus.SUSPENDED,
            CardStatus.PENDING,
        ].includes(cardStatus)
    );
};

export const isCardLocked = ({
    cardStatus,
    userCardStatusReason,
}: {
    cardStatus?: CardStatus;
    userCardStatusReason?: UserCardStatusReason;
}) => {
    return (
        cardStatus != null &&
        cardStatus === CardStatus.SUSPENDED &&
        userCardStatusReason != null &&
        userCardStatusReason === UserCardStatusReason.REQ
    );
};

export const isCardSuspended = ({
    cardStatus,
    userCardStatusReason,
}: {
    cardStatus?: CardStatus;
    userCardStatusReason?: UserCardStatusReason;
}) => {
    return (
        cardStatus != null &&
        cardStatus === CardStatus.SUSPENDED &&
        userCardStatusReason != null &&
        userCardStatusReason !== UserCardStatusReason.REQ
    );
};

export const mapAccountColorToCardColor = (
    color?: string | null,
): CardColor => {
    switch (color) {
        case 'green':
            return CardColor.GREEN;
        case 'red':
            return CardColor.PINK;
        case 'orange':
            return CardColor.YELLOW;
        case 'peach':
            return CardColor.PEACH;
        case 'purple':
            return CardColor.VIOLET;
        case 'blue':
        default:
            return CardColor.BLUE;
    }
};

export const getReadableAccountType = (accountType: AccountType): string => {
    switch (accountType) {
        case AccountType.SAVING:
            return 'Savings';
        case AccountType.CHECKING:
        default:
            return 'Deposit Account';
    }
};

/*
 * Assign a sort value to a bank account, for sorting in the banking tab.  The
 * ordering rules are specified in comments below.
 * The calling sort function is responsible for breaking ties.
 */
const getBankAccountSortValue = (
    account: BankAccountDisplayDetail | AccountDetail,
    moneyMap?: MoneyMapWithRulesAndAccounts,
): number => {
    const hasPhysicalCard =
        account.cards?.some((card) => card.form === CardForm.PHYSICAL) ?? false;

    if (moneyMap?.status === MoneyMapStatus.ACTIVE) {
        /*  Sort order:
            MM Income
            MM Spend & Bills (in MM order)
            Non-MM Existing cash/card "spend"
            MM Goals (in MM order)
            Non-MM Existing cash/card "save"
            Non-MM HYS
         */
        const isIncomeAccount =
            (moneyMap.incomeMoneyMapAccount?.bankAccountId ?? '') ===
            account.bankAccountId;

        // Income account: 0
        if (isIncomeAccount) {
            return 0;
        }

        const isInMoneyMap = moneyMap.moneyMapRules.find(
            (rule) =>
                rule.moneyMapAccount.bankAccountId === account.bankAccountId,
        );

        // Non money map spend accounts: 100
        if (
            !isInMoneyMap &&
            account.holisticCategory === HolisticCategory.SPEND
        ) {
            return 100;
        }

        const moneyMapSpendOrBillsAccountRule = moneyMap.moneyMapRules.find(
            (rule) => {
                return (
                    rule.moneyMapAccount.bankAccountId ===
                        account.bankAccountId &&
                    (rule.type === MoneyMapRuleType.SPEND ||
                        rule.type === MoneyMapRuleType.BILLS)
                );
            },
        );

        // Money map spend accounts: 1 - 99
        if (moneyMapSpendOrBillsAccountRule != null) {
            return moneyMapSpendOrBillsAccountRule.priority;
        }

        const goalAccountRule = moneyMap.moneyMapRules.find(
            (rule) =>
                rule.moneyMapAccount.bankAccountId === account.bankAccountId &&
                rule.type === MoneyMapRuleType.GOAL,
        );

        // Goal accounts: 200 - 299
        if (goalAccountRule != null) {
            return 200 + goalAccountRule.priority;
        }

        // Non money map save accounts: 300
        if (
            account.holisticCategory === HolisticCategory.SAVE &&
            account.accountType !== AccountType.SAVING
        ) {
            return 300;
        }

        // Everything else: 400
        return 400;
    } else {
        /*
        Sort order:
        Cash/card "spend" with physical card
        Cash/card "spend", non-physical card
        Cash/card "save"
        Non-MM HYS
     */
        if (account.accountType === AccountType.CHECKING) {
            if (account.holisticCategory === HolisticCategory.SPEND) {
                if (hasPhysicalCard) {
                    return 1;
                } else {
                    return 2;
                }
            } else {
                return 3;
            }
        } else {
            return 4;
        }
    }
};

export const sortBankAccounts = <
    T extends BankAccountDisplayDetail | AccountDetail,
>(
    bankAccounts: T[],
    moneyMap?: MoneyMapWithRulesAndAccounts,
): T[] => {
    return bankAccounts.sort((a, b) => {
        const aSortVal = getBankAccountSortValue(a, moneyMap);
        const bSortVal = getBankAccountSortValue(b, moneyMap);

        // Sort ties by creation date, oldest first
        if (aSortVal === bSortVal) {
            return (
                new Date(b.creationTime ?? Date.now()).getTime() -
                new Date(a.creationTime ?? Date.now()).getTime()
            );
        }
        return aSortVal - bSortVal;
    });
};

export const getLastFourAccountNumber = (accountNumber: string): string =>
    accountNumber.slice(accountNumber.length - 4);

export const getFruitfulProductNameFromAccountType = (
    accountType: AccountType,
): string => {
    switch (accountType) {
        case AccountType.SAVING:
            return 'Savings';
        case AccountType.CHECKING:
            return 'Cash';
        case AccountType.CHARGE_SECURED:
            return 'Card';
    }
};
